import React, { memo, useCallback } from 'react';

import { IndexPageBase, WebsiteImage } from '@flipdish/api-client-typescript';
import {
  ExpansionPanelDetails,
  FormControl,
  FormHelperText,
  Grid,
  makeStyles,
} from '@material-ui/core';
import { Field, FieldProps, FormikProps, withFormik } from 'formik';
import { isEqual } from 'lodash';
import { Translate } from 'react-localize-redux';
import { connect } from 'react-redux';
import { compose } from 'recompose';

import { formikValidate } from '../../../helpers/validation';
import { createLoadingSelector } from '../../../selectors/loading.selector';
import { Button } from '../../../ui/Button/Button';
import GridContainer from '../../../ui/Layout/GridContainer';
import Spacer from '../../../ui/Spacer';
import TextField from '../../../ui/TextField/TextField';
import {
  DELETE_WEBSITE_SETTINGS_IMAGE,
  deleteWebsiteSettingsImage,
  SET_WEBSITE_SETTINGS,
  setWebsiteSettings,
  UPLOAD_WEBSITE_SETTINGS_IMAGE,
  uploadWebsiteSettingsImage,
} from '../actions';
import EditWebsiteSettingsAboutSectionItem from './EditWebsiteSettingsAboutSectionItem';
import { handleImage } from './utils';

const savingSelector = createLoadingSelector([
  SET_WEBSITE_SETTINGS,
  UPLOAD_WEBSITE_SETTINGS_IMAGE,
  DELETE_WEBSITE_SETTINGS_IMAGE,
]);

const useStyles = makeStyles({
  content: {
    alignItems: 'center',
    flexDirection: 'column',
  },
});

const mapStateToProps = (state: AppState) => {
  return {
    saving: savingSelector(state),
  };
};

const mapDispatchToProps = (dispatch: ThunkDispatch) => ({
  setSettings: (settings: IndexPageBase) => dispatch(setWebsiteSettings(settings)),
  uploadImage: (imageLocation: WebsiteImage.ImageLocationEnum, file) =>
    dispatch(uploadWebsiteSettingsImage(imageLocation, file)),
  deleteImage: (id: number) => dispatch(deleteWebsiteSettingsImage(id)),
});

type FormValues = IndexPageBase & {
  AboutSectionLeftImage?: WebsiteImage | File;
  AboutSectionRightImage?: WebsiteImage | File;
};

type OuterProps = {
  settings: IndexPageBase;
  AboutSectionLeftImage?: WebsiteImage | File;
  AboutSectionRightImage?: WebsiteImage | File;
};

type InnerProps = FormikProps<FormValues> &
  ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps>;

type Props = InnerProps & OuterProps;

const validateText = (content: string) => {
  return formikValidate.containsHTMLElement(content);
};

export default compose<InnerProps, OuterProps>(
  connect(mapStateToProps, mapDispatchToProps),
  withFormik<Props, FormValues>({
    displayName: ' EditWebsiteSettingsAboutPanelForm',
    enableReinitialize: true,
    mapPropsToValues(props) {
      const { settings, AboutSectionRightImage, AboutSectionLeftImage } = props;

      return {
        AboutSectionRightImage,
        AboutSectionLeftImage,
        ...settings,
      };
    },
    handleSubmit(values, formikBag) {
      const { AboutSectionLeftImage, AboutSectionRightImage, ...mainSettings } = values;
      const {
        props: {
          setSettings,
          uploadImage,
          deleteImage,
          AboutSectionRightImage: OriginalAboutSectionRightImage,
          AboutSectionLeftImage: OriginalAboutSectionLeftImage,
          settings: originalSettings,
        },
      } = formikBag;

      handleImage(
        uploadImage,
        deleteImage,
        WebsiteImage.ImageLocationEnum.IndexAboutSectionRight,
        AboutSectionRightImage,
        OriginalAboutSectionRightImage
      );
      handleImage(
        uploadImage,
        deleteImage,
        WebsiteImage.ImageLocationEnum.IndexAboutSectionLeft,
        AboutSectionLeftImage,
        OriginalAboutSectionLeftImage
      );

      if (!isEqual(mainSettings, originalSettings)) {
        setSettings(mainSettings);
      }
    },
  })
)(
  memo(function EditWebsiteSettingsAboutSectionsPanel(props: Props) {
    const {
      errors,
      submitForm,
      values: {
        AboutSectionLeftBody,
        AboutSectionLeftTitle,
        AboutSectionRightBody,
        AboutSectionRightTitle,
        AboutSectionLeftImage,
        AboutSectionRightImage,
      },
      saving,
    } = props;
    const classes = useStyles();
    const handleSave = useCallback(submitForm, []);

    return (
      <ExpansionPanelDetails className={classes.content}>
        <Field name="AboutSectionTitle" validate={(value: string) => validateText(value)}>
          {(fProps: FieldProps) => {
            const { field, form } = fProps;

            return (
              <FormControl fullWidth>
                <TextField
                  value={field.value || ''}
                  fdKey="about_section_title"
                  label={<Translate id="About_section_title" />}
                  onChange={(e) => {
                    form.setFieldValue(field.name, e.target.value);
                  }}
                />
                <FormHelperText error>{errors['AboutSectionTitle']}</FormHelperText>
              </FormControl>
            );
          }}
        </Field>
        <Spacer size={16} />
        <Field name="AboutSectionSubtitle" validate={(value: string) => validateText(value)}>
          {(fProps: FieldProps) => {
            const { field, form } = fProps;

            return (
              <FormControl fullWidth>
                <TextField
                  value={field.value || ''}
                  fdKey="about_section_subtitle"
                  label={<Translate id="About_section_subtitle" />}
                  onChange={(e) => {
                    form.setFieldValue(field.name, e.target.value);
                  }}
                />
                <FormHelperText error>{errors['AboutSectionSubtitle']}</FormHelperText>
              </FormControl>
            );
          }}
        </Field>
        <Spacer size={16} />
        <GridContainer>
          <Grid item md={12} sm={6}>
            <EditWebsiteSettingsAboutSectionItem
              titleFieldName="AboutSectionLeftTitle"
              contentFieldName="AboutSectionLeftBody"
              imageFieldName="AboutSectionRightImage"
              title={AboutSectionLeftTitle || ''}
              content={AboutSectionLeftBody || ''}
              errors={errors}
              image={AboutSectionLeftImage}
            />
          </Grid>
          <Grid item md={12} sm={6}>
            <EditWebsiteSettingsAboutSectionItem
              titleFieldName="AboutSectionRightTitle"
              contentFieldName="AboutSectionRightBody"
              imageFieldName="AboutSectionLeftImage"
              title={AboutSectionRightTitle || ''}
              content={AboutSectionRightBody || ''}
              errors={errors}
              image={AboutSectionRightImage}
            />
          </Grid>
        </GridContainer>
        <Grid container justify="flex-end">
          <Button
            color="primary"
            variant="outlined"
            fdKey="save_about_section"
            onClick={handleSave}
            disabled={saving}
          >
            <Translate id="Save" />
          </Button>
        </Grid>
      </ExpansionPanelDetails>
    );
  })
);
